/*
 * Ztove Configurator v2 — kitchen-fit assistant
 * Six steps: Kitchen → Hob → Venting → Fit → Cookware → Summary.
 * Each step asks situational questions; the recommendation engine
 * surfaces the right product with confidence + rationale.
 */
(() => {
  if (window.__ztoveConfiguratorV2Init) return;
  window.__ztoveConfiguratorV2Init = true;

  const root = document.querySelector('[data-ztove-configurator-v2]');
  if (!root) return;

  let products = [];
  let copy = {};
  try {
    products = JSON.parse(root.querySelector('[data-ztcfg2-products]').textContent);
    copy = JSON.parse(root.querySelector('[data-ztcfg2-copy]').textContent);
  } catch (e) {
    console.error('[ztcfg2] data parse failed', e);
    return;
  }

  const moneyFormat = copy.money_format || '${{amount}}';
  const productByKey = {};
  products.forEach((p) => (productByKey[p.key] = p));

  const hobs = products.filter((p) => p.role === 'hob');
  const HOB_60 = hobs.find((h) => /60\s*cm/i.test(h.title));
  const HOB_80 = hobs.find((h) => /80\s*cm/i.test(h.title) && !/downdraft/i.test(h.title));
  const HOB_DD = hobs.find((h) => /downdraft/i.test(h.title));

  const ICONS = {
    check:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 5 5L20 7"/></svg>',
    plus:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg>',
    minus:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/></svg>',
  };

  const STEPS = ['kitchen', 'hob', 'venting', 'fit', 'cookware', 'summary'];
  const STEP_LABELS = copy.step_labels || {
    kitchen: 'Kitchen', hob: 'Hob', venting: 'Venting',
    fit: 'Fit', cookware: 'Cookware', summary: 'Summary',
  };
  const COPY_STEPS = copy.steps || {};
  const COPY_CONF = copy.confidence || {};
  const COPY_NOTICES = copy.notices || {};
  const COPY_RAT = copy.rationale || {};
  const COPY_METHODS = copy.venting_methods || {};
  const COPY_SPECS = copy.specs || {
    '60': { w: 560, d: 490, h: 49, label: '560 × 490 × 49 mm' },
    '80': { w: 760, d: 490, h: 49, label: '750–770 × 490 × 49 mm' },
    'dd': { w: 754, d: 494, h: 254, label: '754 × 494 × 254 mm' },
  };
  const TOL_W = copy.tolerance_w ?? 10;
  const TOL_D = copy.tolerance_d ?? 5;
  const VENT_TBL = copy.venting_table || {
    duct_under_1m: 1, duct_1_3m: 2, duct_over_3m: 3,
    joiner_under_1m: 0, joiner_1_3m: 1, joiner_over_3m: 2,
    bend_0_1: 1, bend_2_3: 2, bend_4_plus: 4,
  };

  const t = (path, fallback = '') =>
    path.split('.').reduce((o, k) => (o == null ? o : o[k]), copy) ?? fallback;

  // ── State ────────────────────────────────────────────────────────────────
  const state = {
    step: 'kitchen',
    kitchen: {
      projectType: null, // 'new_kitchen' | 'replacement' | 'unknown'
      placement: null, // 'wall' | 'island' | 'peninsula' | 'unknown'
      existingExtractor: null, // 'yes' | 'no' | 'want_downdraft' | 'unknown'
    },
    hobPrefs: {
      sizeClass: null, // '60' | '80' | 'free' | 'unknown'
      cookingFrequency: null, // 'rare' | 'sometimes' | 'often'
      override: null, // user-picked hob key (overrides recommendation)
    },
    venting: {
      outsideVenting: null, // 'yes_wall' | 'yes_floor' | 'maybe' | 'no'
      routeLength: null, // 'under_1m' | '1_3m' | 'over_3m' | 'unknown'
      bends: null, // '0_1' | '2_3' | '4_plus' | 'unknown'
      cabinetType: null, // 'standard_plinth' | 'drawer' | 'island' | 'unknown'
      methodOverride: null, // override the engine's method
      partsAdjust: {}, // { partKey: deltaQty } — user manual edits
    },
    fit: {
      currentHobWidth: '',
      currentHobDepth: '',
      cutoutWidth: '',
      cutoutDepth: '',
      mounting: null, // 'surface' | 'flush' | 'unknown'
      cabinetWidth: '',
      spaceBelow: '',
    },
    cookware: {
      wantsTempControl: null, // 'yes' | 'maybe' | 'no'
      cookingStyles: [], // multi-select
      householdSize: null, // 1 | 2 | '3_4' | '5_plus'
      bundleOverride: null, // user-picked bundle key
    },
    submitting: false,
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    const value = (cents / 100).toFixed(2);
    return moneyFormat
      .replace(/{{\s*amount\s*}}/g, value)
      .replace(/{{\s*amount_no_decimals\s*}}/g, Math.round(cents / 100).toString())
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, value.replace('.', ','));
  }

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Recommendation engine ────────────────────────────────────────────────
  function recommendHob() {
    const k = state.kitchen;
    const h = state.hobPrefs;
    const why = [];
    let key = null;
    let confidence = 'green';

    if (k.existingExtractor === 'want_downdraft') {
      key = HOB_DD?.key || null;
      why.push(COPY_RAT.hob_downdraft || 'You want built-in downdraft venting — no separate range hood needed.');
    } else if (h.sizeClass === '60') {
      key = HOB_60?.key || null;
      why.push(COPY_RAT.hob_60 || 'Fits a standard 60 cm cut-out (560 × 490 mm).');
    } else if (h.sizeClass === '80') {
      key = HOB_80?.key || null;
      why.push(COPY_RAT.hob_80 || 'Designed for 80 cm cut-outs (750–770 × 490 mm).');
    } else if (k.projectType === 'new_kitchen' && h.cookingFrequency === 'often') {
      key = HOB_80?.key || null;
      why.push(COPY_RAT.hob_new_kitchen_often || 'A new kitchen plus frequent large-pan cooking — the 80 cm gives you five zones.');
    } else if (h.sizeClass === 'free' && h.cookingFrequency === 'often') {
      key = HOB_80?.key || null;
      why.push(COPY_RAT.hob_free_often || 'You cook often with multiple large pans — 80 cm earns its space.');
    } else {
      key = HOB_60?.key || null;
      why.push(COPY_RAT.hob_default || 'Compact 60 cm — covers everyday cooking and replaces a standard hob 1:1.');
    }

    if (h.cookingFrequency === 'often' && key === HOB_60?.key) {
      why.push(COPY_RAT.hob_60_step_up || 'If you frequently cook with multiple big pans, consider stepping up to 80 cm.');
      confidence = 'yellow';
    }

    if (k.placement === 'island' && key !== HOB_DD?.key) {
      why.push(COPY_RAT.hob_island_warning || 'Heads up: an island typically needs a built-in downdraft or ceiling extractor.');
      confidence = 'yellow';
    }

    if (!key) {
      key = HOB_60?.key || null;
      confidence = 'red';
    }

    const product = key ? productByKey[key] : null;
    return { key, product, why, confidence };
  }

  function recommendVenting() {
    const v = state.venting;
    const why = [];
    let method = null;
    let confidence = 'green';

    const outsideOk = v.outsideVenting === 'yes_wall' || v.outsideVenting === 'yes_floor';
    const longRoute = v.routeLength === 'over_3m' || v.bends === '4_plus';

    if (outsideOk) {
      method = 'ducted_extraction';
      why.push(COPY_METHODS.ducted_extraction?.primary_why || 'You can route air outside — this is the best-performing option, with no filter to maintain.');
      if (longRoute) {
        why.push(COPY_METHODS.ducted_extraction?.long_route_warning || 'For a longer route or many bends, ask your installer about a Ø150 round pipe to keep airflow strong.');
        confidence = 'yellow';
      }
    } else if (v.outsideVenting === 'maybe') {
      method = 'ducted_extraction';
      why.push(COPY_METHODS.ducted_extraction?.primary_why || 'Outside venting may be possible — get an installer to confirm the route before cutting.');
      confidence = 'yellow';
    } else if (v.cabinetType === 'standard_plinth' || v.cabinetType === 'drawer') {
      method = 'ducted_recirculation';
      why.push(COPY_METHODS.ducted_recirculation?.primary_why || 'Air routes through ducting to a plinth-mounted carbon filter and returns cleaned to the room.');
      const note = COPY_METHODS.ducted_recirculation?.filter_note;
      if (note) why.push(note);
    } else {
      method = 'pipeless_recirculation';
      why.push(COPY_METHODS.pipeless_recirculation?.primary_why || 'Pipeless filtering: cut a 230 × 82 mm hole in the rear cabinet wall, no ducting needed.');
      const note = COPY_METHODS.pipeless_recirculation?.filter_note;
      if (note) why.push(note);
    }

    if (v.methodOverride) method = v.methodOverride;

    // Compute parts based on chosen method.
    const parts = {};
    if (method === 'ducted_extraction') {
      // Quantities sourced from settings (VENT_TBL).
      const ductByLength = {
        under_1m: VENT_TBL.duct_under_1m,
        '1_3m': VENT_TBL.duct_1_3m,
        over_3m: VENT_TBL.duct_over_3m,
        unknown: VENT_TBL.duct_1_3m,
      };
      const joinerByLength = {
        under_1m: VENT_TBL.joiner_under_1m,
        '1_3m': VENT_TBL.joiner_1_3m,
        over_3m: VENT_TBL.joiner_over_3m,
        unknown: VENT_TBL.joiner_1_3m,
      };
      const cornersByBends = {
        '0_1': VENT_TBL.bend_0_1,
        '2_3': VENT_TBL.bend_2_3,
        '4_plus': VENT_TBL.bend_4_plus,
        unknown: VENT_TBL.bend_2_3,
      };
      const dq = ductByLength[v.routeLength] ?? VENT_TBL.duct_1_3m ?? 2;
      const jq = joinerByLength[v.routeLength] ?? VENT_TBL.joiner_1_3m ?? 1;
      if (dq > 0 && productByKey['duct_1m']) parts['duct_1m'] = dq;
      if (jq > 0 && productByKey['duct_connector']) parts['duct_connector'] = jq;
      const corners = cornersByBends[v.bends] || 0;
      if (corners > 0 && productByKey['connector_horizontal_90']) {
        parts['connector_horizontal_90'] = corners;
      }
      if (productByKey['flexible_duct_024']) parts['flexible_duct_024'] = 1;
      if (productByKey['trough_wall_grille']) parts['trough_wall_grille'] = 1;
      if (productByKey['exterior_grille']) parts['exterior_grille'] = 1;
    } else if (method === 'ducted_recirculation' || method === 'pipeless_recirculation') {
      if (productByKey['recirculation_kit']) parts['recirculation_kit'] = 1;
      if (method === 'ducted_recirculation') {
        if (productByKey['duct_1m']) parts['duct_1m'] = 1;
        if (productByKey['flexible_duct_024']) parts['flexible_duct_024'] = 1;
      }
    }

    // Apply user manual qty adjustments
    Object.entries(state.venting.partsAdjust).forEach(([k, delta]) => {
      const next = (parts[k] || 0) + delta;
      if (next > 0) parts[k] = next;
      else delete parts[k];
    });

    return { method, why, confidence, parts };
  }

  function recommendCookware() {
    const c = state.cookware;
    const why = [];
    let bundles = [];
    let confidence = 'green';

    if (c.wantsTempControl === 'no') {
      why.push(COPY_RAT.ck_no_temp || 'You opted out of temperature control — your existing induction cookware will work, just without the precision features.');
      return { bundles: [], why, confidence: 'yellow' };
    }

    const big = c.householdSize === '3_4' || c.householdSize === '5_plus';
    const wantsSousVide = c.cookingStyles.includes('sous_vide');
    const wantsDeepFry = c.cookingStyles.includes('deep_frying');
    const wantsFry = c.cookingStyles.includes('frying');

    if (big || wantsSousVide || wantsDeepFry) {
      bundles = ['bundle_starter', 'bundle_full'];
      why.push(COPY_RAT.ck_full || 'Both bundles together cover sous vide, deep frying, and family-sized meals end to end.');
    } else if (c.wantsTempControl === 'yes' && wantsFry) {
      bundles = ['bundle_starter', 'bundle_full'];
      why.push(COPY_RAT.ck_frying || 'Starter + Full Experience gives you a 24 cm and 28 cm pan plus three pots — ideal for a frying-heavy kitchen.');
    } else if (c.wantsTempControl === 'maybe') {
      bundles = ['bundle_starter'];
      why.push(COPY_RAT.ck_starter_maybe || 'Starter Bundle is the lightest way to unlock TrueTemp™ — one pot and one pan.');
      confidence = 'yellow';
    } else {
      bundles = ['bundle_starter'];
      why.push(COPY_RAT.ck_starter || 'Starter Bundle covers everyday meals with a 3 L pot and a 24 cm non-stick pan.');
    }

    if (c.wantsTempControl === null) confidence = 'red';

    bundles = bundles.filter((b) => productByKey[b]);
    return { bundles, why, confidence };
  }

  function overallConfidence() {
    const r = computeRecommendations();
    const levels = ['green', 'yellow', 'red'];
    let max = 'green';
    [r.hob.confidence, r.venting?.confidence, r.cookware.confidence].forEach((c) => {
      if (!c) return;
      if (levels.indexOf(c) > levels.indexOf(max)) max = c;
    });
    return max;
  }

  function computeRecommendations() {
    const hobRec = recommendHob();
    const isDowndraft = (state.hobPrefs.override || hobRec.key) === HOB_DD?.key;
    const ventingRec = isDowndraft ? recommendVenting() : null;
    const cookwareRec = recommendCookware();
    return { hob: hobRec, venting: ventingRec, cookware: cookwareRec, isDowndraft };
  }

  function effectiveHob() {
    const override = state.hobPrefs.override;
    if (override && productByKey[override]) return productByKey[override];
    const rec = recommendHob();
    return rec.product;
  }

  function effectiveBundles() {
    if (state.cookware.bundleOverride === 'none') return [];
    if (state.cookware.bundleOverride && productByKey[state.cookware.bundleOverride]) {
      return [productByKey[state.cookware.bundleOverride]];
    }
    const rec = recommendCookware();
    return rec.bundles.map((k) => productByKey[k]).filter(Boolean);
  }

  // ── Cart items + totals ──────────────────────────────────────────────────
  function getCartItems() {
    const items = [];
    const hob = effectiveHob();
    if (hob) items.push({ id: hob.variant_id, quantity: 1, _product: hob, _section: 'Hob' });

    const isDD = hob?.key === HOB_DD?.key;
    if (isDD) {
      const venting = recommendVenting();
      Object.entries(venting.parts || {}).forEach(([k, q]) => {
        const p = productByKey[k];
        if (p && q > 0) items.push({ id: p.variant_id, quantity: q, _product: p, _section: 'Venting' });
      });
    }
    effectiveBundles().forEach((p) => {
      items.push({ id: p.variant_id, quantity: 1, _product: p, _section: 'Cookware' });
    });
    return items;
  }

  function getSubtotal() {
    return getCartItems().reduce((s, i) => s + (i._product.price || 0) * i.quantity, 0);
  }

  function updateTotals() {
    const subtotal = getSubtotal();
    const formatted = subtotal > 0 ? formatMoney(subtotal) : '—';
    const a = root.querySelector('[data-ztcfg2-total]');
    const b = root.querySelector('[data-ztcfg2-mobile-total]');
    if (a) a.textContent = formatted;
    if (b) b.textContent = formatted;
    updateViz();
  }

  // ── Visualization ────────────────────────────────────────────────────────
  // Spec geometries — px scale matches the SVG viewBox (520 × 540).
  // Real-world cut-outs (mm) are mapped to px via a constant scale (~0.4 px/mm).
  const VIZ_SPEC_MM = COPY_SPECS;
  const VIZ_SPEC_PX = {
    '60': { x: 146, y: 220, w: 224, h: 196 },
    '80': { x: 110, y: 220, w: 300, h: 196 },
    'dd': { x: 110, y: 220, w: 302, h: 198 },
  };
  const VIZ_PX_PER_MM_W = 0.4;
  const VIZ_PX_PER_MM_D = 0.4;
  const VIZ_CENTER_X = 260;
  const VIZ_CENTER_Y = 318;

  function vizHobKey() {
    const hob = effectiveHob();
    if (!hob) return null;
    if (hob.key === HOB_60?.key) return '60';
    if (hob.key === HOB_80?.key) return '80';
    if (hob.key === HOB_DD?.key) return 'dd';
    return null;
  }

  function updateViz() {
    const svg = root.querySelector('.ztcfg2-viz');
    const status = root.querySelector('[data-ztcfg2-viz-status]');
    const captionEyebrow = root.querySelector('[data-viz-eyebrow]');
    const captionLine = root.querySelector('[data-viz-line]');
    if (!svg) return;

    const hk = vizHobKey();

    // Toggle hob key — drives spec dashed outline + downdraft indicator
    if (hk) svg.setAttribute('data-hob-key', hk);
    else svg.removeAttribute('data-hob-key');

    // Hood: visible when chosen hob is non-downdraft
    if (hk === '60' || hk === '80') svg.setAttribute('data-hood', 'standard');
    else svg.removeAttribute('data-hood');

    // Cookware silhouettes
    const showCookware =
      ['cookware', 'summary'].includes(state.step) &&
      effectiveBundles().length > 0;
    if (showCookware) svg.setAttribute('data-cookware', 'show');
    else svg.removeAttribute('data-cookware');

    // User cut-out rect — driven by entered cutoutWidth/Depth
    const cutoutRect = svg.querySelector('[data-ztcfg2-cutout-rect]');
    const cw = parseInt(state.fit.cutoutWidth, 10);
    const cd = parseInt(state.fit.cutoutDepth, 10);
    let match = null;
    const dimText = (id) => svg.querySelector(`[data-dim-text="${id}"]`);
    const widthLabel = dimText('w');
    const depthLabel = dimText('d');

    if (hk && cw > 0 && cd > 0 && cutoutRect) {
      // Compute the entered rect, centered on (VIZ_CENTER_X, VIZ_CENTER_Y).
      const wPx = Math.max(40, Math.min(360, cw * VIZ_PX_PER_MM_W));
      const hPx = Math.max(40, Math.min(220, cd * VIZ_PX_PER_MM_D));
      cutoutRect.setAttribute('x', VIZ_CENTER_X - wPx / 2);
      cutoutRect.setAttribute('y', VIZ_CENTER_Y - hPx / 2);
      cutoutRect.setAttribute('width', wPx);
      cutoutRect.setAttribute('height', hPx);
      svg.setAttribute('data-cutout', 'show');

      // Match check vs spec using configured tolerances
      const spec = VIZ_SPEC_MM[hk];
      const wMatch = Math.abs(cw - spec.w) <= TOL_W;
      const dMatch = Math.abs(cd - spec.d) <= TOL_D;
      match = wMatch && dMatch;
      svg.setAttribute('data-cutout-match', match ? 'match' : 'mismatch');

      // Update labels with user values
      if (widthLabel) widthLabel.textContent = `${cw} mm`;
      if (depthLabel) depthLabel.textContent = `${cd} mm`;
    } else {
      svg.removeAttribute('data-cutout');
      svg.removeAttribute('data-cutout-match');
      // Show spec dimensions as defaults when a hob is chosen
      if (hk) {
        const spec = VIZ_SPEC_MM[hk];
        if (widthLabel) widthLabel.textContent = `${spec.w} mm`;
        if (depthLabel) depthLabel.textContent = `${spec.d} mm`;
      } else {
        if (widthLabel) widthLabel.textContent = '—';
        if (depthLabel) depthLabel.textContent = '—';
      }
    }

    // Status indicator (top-right of viz frame)
    if (status) {
      status.classList.remove('is-match', 'is-mismatch');
      if (match === true) {
        status.textContent = 'Cut-out matches spec';
        status.classList.add('is-match');
      } else if (match === false) {
        status.textContent = 'Cut-out off — confirm with installer';
        status.classList.add('is-mismatch');
      } else if (hk) {
        status.textContent = 'Target shown';
      } else {
        status.textContent = '';
      }
    }

    // Caption — short context line per step
    const stepIdx = visibleSteps().indexOf(state.step) + 1;
    const totalSteps = visibleSteps().length;
    if (captionEyebrow) captionEyebrow.textContent = `Step ${stepIdx} of ${totalSteps}`;
    if (captionLine) {
      const lines = {
        kitchen: 'Tell us about your kitchen — we’ll start drawing your setup.',
        hob: hk
          ? `Target cut-out: ${VIZ_SPEC_MM[hk].w} × ${VIZ_SPEC_MM[hk].d} mm. The dashed outline is what your worktop needs.`
          : 'Pick a hob — we’ll draw the cut-out it needs.',
        venting: hk === 'dd'
          ? 'The downdraft pulls air down through the slot in the centre.'
          : 'Your hob pairs with a separate range hood, drawn above.',
        fit: cw && cd
          ? (match ? 'Your cut-out matches the spec.' : 'Your cut-out is off the spec — your installer should confirm.')
          : 'Type your cut-out dimensions to overlay them on the target.',
        cookware: 'Two zones in use — pot left, pan right. Smart cookware enables 0–250 °C control.',
        summary: 'Your full Ztove setup, ready to add to bag.',
      };
      captionLine.textContent = lines[state.step] || '';
    }
  }

  // ── Step navigation ──────────────────────────────────────────────────────
  function visibleSteps() {
    const isDowndraft = effectiveHob()?.key === HOB_DD?.key;
    return STEPS.filter((s) => s !== 'venting' || isDowndraft);
  }

  function setStep(step) {
    if (!STEPS.includes(step)) return;
    state.step = step;
    root.querySelectorAll('[data-step]').forEach((view) => {
      view.classList.toggle('is-active', view.dataset.step === step);
    });
    if (step === 'kitchen') renderKitchen();
    if (step === 'hob') renderHob();
    if (step === 'venting') renderVenting();
    if (step === 'fit') renderFit();
    if (step === 'cookware') renderCookware();
    if (step === 'summary') renderSummary();
    updateRail();
    updateTotals();
    const stage = root.querySelector('[data-ztcfg2-stage]');
    if (stage) stage.scrollTop = 0;
  }

  function nextStep() {
    if (!canProceed(state.step)) return;
    const order = visibleSteps();
    const idx = order.indexOf(state.step);
    if (idx < order.length - 1) setStep(order[idx + 1]);
  }

  function prevStep() {
    const order = visibleSteps();
    const idx = order.indexOf(state.step);
    if (idx > 0) setStep(order[idx - 1]);
  }

  function canProceed(step) {
    if (step === 'kitchen') {
      const k = state.kitchen;
      return !!(k.projectType && k.placement && k.existingExtractor);
    }
    if (step === 'hob') {
      const h = state.hobPrefs;
      return !!(h.sizeClass && h.cookingFrequency);
    }
    if (step === 'venting') {
      const v = state.venting;
      return !!(v.outsideVenting && v.routeLength && v.bends && v.cabinetType);
    }
    if (step === 'fit') {
      // fit is optional — measurements help confidence but don't block
      return true;
    }
    if (step === 'cookware') {
      return state.cookware.wantsTempControl !== null;
    }
    return false;
  }

  function updateRail() {
    const order = visibleSteps();
    const idx = order.indexOf(state.step);
    const allSteps = STEPS;
    const isDowndraft = effectiveHob()?.key === HOB_DD?.key;

    root.querySelectorAll('[data-step-rail]').forEach((el) => {
      const key = el.dataset.stepRail;
      const isVisible = order.includes(key);
      el.classList.toggle('is-skipped', !isVisible);
      if (!isVisible) {
        el.classList.remove('is-active', 'is-done', 'is-clickable');
        return;
      }
      const elIdx = order.indexOf(key);
      el.classList.toggle('is-active', elIdx === idx);
      el.classList.toggle('is-done', elIdx < idx);
      el.classList.toggle('is-clickable', elIdx < idx);
    });

    const progress = root.querySelector('[data-ztcfg2-progress]');
    if (progress) progress.style.width = `${((idx + 1) / order.length) * 100}%`;

    const back = root.querySelector('[data-ztcfg2-back]');
    if (back) back.disabled = idx === 0;

    const mStep = root.querySelector('[data-ztcfg2-mobile-step]');
    if (mStep) mStep.textContent = `Step ${idx + 1} · ${STEP_LABELS[state.step]}`;

    const primary = root.querySelector('[data-ztcfg2-primary]');
    const label = root.querySelector('[data-ztcfg2-primary-label]');
    if (primary && label) {
      if (state.step === 'summary') {
        label.textContent = 'Add everything to bag';
        primary.disabled = getCartItems().length === 0;
      } else if (state.step === 'cookware') {
        label.textContent = 'See your setup';
        primary.disabled = !canProceed(state.step);
      } else {
        label.textContent = 'Continue';
        primary.disabled = !canProceed(state.step);
      }
    }
  }

  // ── Question chip UI helper ─────────────────────────────────────────────
  let chipGroupCounter = 0;

  function chipGroup(question, help, options, currentValue, onSelect) {
    const gid = `cg${++chipGroupCounter}`;
    const chips = options
      .map(
        (o) =>
          `<button type="button" class="ztcfg2__chip ${currentValue === o.value ? 'is-active' : ''}" data-chip="${escapeHtml(o.value)}" data-chip-group="${gid}">${escapeHtml(o.label)}</button>`
      )
      .join('');
    const html = `
      <div class="ztcfg2__qgroup" data-qgroup="${gid}">
        <h3 class="ztcfg2__qgroup-q">${escapeHtml(question)}</h3>
        ${help ? `<p class="ztcfg2__qgroup-help">${escapeHtml(help)}</p>` : ''}
        <div class="ztcfg2__chips">${chips}</div>
      </div>`;
    return { html, bind: (root) => {
      root.querySelectorAll(`[data-chip-group="${gid}"]`).forEach((b) => {
        b.addEventListener('click', () => onSelect(b.dataset.chip));
      });
    }};
  }

  function multiChipGroup(question, help, options, currentArr, onChange) {
    const gid = `mcg${++chipGroupCounter}`;
    const chips = options
      .map(
        (o) =>
          `<button type="button" class="ztcfg2__chip ${currentArr.includes(o.value) ? 'is-active' : ''}" data-mchip="${escapeHtml(o.value)}" data-mchip-group="${gid}">${escapeHtml(o.label)}</button>`
      )
      .join('');
    const html = `
      <div class="ztcfg2__qgroup" data-qgroup="${gid}">
        <h3 class="ztcfg2__qgroup-q">${escapeHtml(question)}</h3>
        ${help ? `<p class="ztcfg2__qgroup-help">${escapeHtml(help)}</p>` : ''}
        <div class="ztcfg2__chips">${chips}</div>
      </div>`;
    return { html, bind: (rt) => {
      rt.querySelectorAll(`[data-mchip-group="${gid}"]`).forEach((b) => {
        b.addEventListener('click', () => {
          const v = b.dataset.mchip;
          const i = currentArr.indexOf(v);
          if (i >= 0) currentArr.splice(i, 1);
          else currentArr.push(v);
          onChange();
        });
      });
    }};
  }

  // ── Step 1: Kitchen ──────────────────────────────────────────────────────
  function renderKitchen() {
    const wrap = root.querySelector('[data-ztcfg2-step-kitchen]');
    if (!wrap) return;
    const k = state.kitchen;

    const g1 = chipGroup(
      'What kind of project is this?',
      null,
      [
        { value: 'new_kitchen', label: 'A new kitchen' },
        { value: 'replacement', label: 'Replacing a hob' },
        { value: 'unknown', label: 'Not sure yet' },
      ],
      k.projectType,
      (v) => { k.projectType = v; renderKitchen(); updateRail(); }
    );
    const g2 = chipGroup(
      'Where will the hob go?',
      null,
      [
        { value: 'wall', label: 'Against a wall' },
        { value: 'island', label: 'On an island' },
        { value: 'peninsula', label: 'Peninsula' },
        { value: 'unknown', label: 'Not sure' },
      ],
      k.placement,
      (v) => { k.placement = v; renderKitchen(); updateRail(); }
    );
    const g3 = chipGroup(
      'Ventilation today?',
      'A regular hob (60/80 cm) needs a separate range hood. The downdraft hob has venting built in.',
      [
        { value: 'yes', label: 'I have a range hood' },
        { value: 'no', label: 'No extractor yet' },
        { value: 'want_downdraft', label: 'I want built-in downdraft' },
        { value: 'unknown', label: 'Not sure' },
      ],
      k.existingExtractor,
      (v) => { k.existingExtractor = v; renderKitchen(); updateRail(); }
    );

    wrap.innerHTML = `
      <div class="ztcfg2__view-head">
        <h1 class="ztcfg2__view-title">${escapeHtml(COPY_STEPS.kitchen?.heading || 'Tell us about your kitchen')}</h1>
        <p class="ztcfg2__view-sub">${escapeHtml(COPY_STEPS.kitchen?.sub || 'Three quick details so we can recommend the right Ztove setup — and flag anything an installer should double-check.')}</p>
      </div>
      ${g1.html}${g2.html}${g3.html}`;
    g1.bind(wrap); g2.bind(wrap); g3.bind(wrap);
  }

  // ── Step 2: Hob ──────────────────────────────────────────────────────────
  function renderHob() {
    const wrap = root.querySelector('[data-ztcfg2-step-hob]');
    if (!wrap) return;
    const h = state.hobPrefs;

    const g1 = chipGroup(
      'Available hob width',
      'Measure the existing cut-out, or pick "I can choose" for a new build.',
      [
        { value: '60', label: '60 cm class' },
        { value: '80', label: '80 cm class' },
        { value: 'free', label: 'I can choose freely' },
        { value: 'unknown', label: "I don't know" },
      ],
      h.sizeClass,
      (v) => { h.sizeClass = v; h.override = null; renderHob(); updateRail(); updateTotals(); }
    );
    const g2 = chipGroup(
      'How often do you cook with multiple large pans?',
      null,
      [
        { value: 'rare', label: 'Rarely' },
        { value: 'sometimes', label: 'Sometimes' },
        { value: 'often', label: 'Often' },
      ],
      h.cookingFrequency,
      (v) => { h.cookingFrequency = v; h.override = null; renderHob(); updateRail(); updateTotals(); }
    );

    const headHtml = `
      <div class="ztcfg2__view-head">
        <h1 class="ztcfg2__view-title">${escapeHtml(COPY_STEPS.hob?.heading || 'Pick your hob')}</h1>
        <p class="ztcfg2__view-sub">${escapeHtml(COPY_STEPS.hob?.sub || "Two questions — we'll recommend the best fit and let you swap if you'd prefer something else.")}</p>
      </div>`;

    let recHtml = '';
    let altsHtml = '';
    if (canProceed('hob')) {
      const rec = recommendHob();
      const chosenKey = h.override || rec.key;
      const chosen = productByKey[chosenKey];
      if (chosen) {
        recHtml = recommendationCard({
          eyebrow: h.override ? 'Your pick' : 'Recommended for you',
          confidence: rec.confidence,
          product: chosen,
          why: h.override ? ['You overrode our recommendation. We respect that.'] : rec.why,
        });
      }
      altsHtml = `
        <div class="ztcfg2__override">
          <div class="ztcfg2__override-head">
            <span class="ztcfg2__override-title">Or pick a different hob</span>
          </div>
          <div class="ztcfg2__override-grid">
            ${hobs
              .map((hob) => {
                const active = chosenKey === hob.key;
                const sizeBadge = /downdraft/i.test(hob.title) ? 'Downdraft 80 cm' : (/(\d{2,3})\s*cm/i.exec(hob.title) || [])[1] ? `${(/(\d{2,3})\s*cm/i.exec(hob.title) || [])[1]} cm` : '';
                return `
                  <button type="button" class="ztcfg2__alt ${active ? 'is-active' : ''}" data-alt-hob="${hob.key}">
                    <span class="ztcfg2__alt-thumb">${hob.featured_image_thumb ? `<img src="${hob.featured_image_thumb}" alt="" loading="lazy" />` : ''}</span>
                    <span class="ztcfg2__alt-text">
                      <span class="ztcfg2__alt-title">${escapeHtml(hob.title)}</span>
                      <span class="ztcfg2__alt-sub">${escapeHtml(sizeBadge)}</span>
                    </span>
                    <span class="ztcfg2__alt-price">${formatMoney(hob.price)}</span>
                  </button>`;
              })
              .join('')}
          </div>
        </div>`;
    }

    wrap.innerHTML = `${headHtml}${g1.html}${g2.html}${recHtml}${altsHtml}`;
    g1.bind(wrap); g2.bind(wrap);
    wrap.querySelectorAll('[data-alt-hob]').forEach((b) => {
      b.addEventListener('click', () => {
        h.override = b.dataset.altHob;
        renderHob(); updateRail(); updateTotals();
      });
    });
  }

  function recommendationCard({ eyebrow, confidence, product, why }) {
    if (!product) return '';
    const confLabel =
      confidence === 'green' ? (COPY_CONF.green || 'Good fit')
      : confidence === 'yellow' ? (COPY_CONF.yellow || 'Likely fit · check with installer')
      : (COPY_CONF.red || 'Needs review');
    const conf = `<span class="ztcfg2__rec-confidence is-${confidence}">${escapeHtml(confLabel)}</span>`;
    return `
      <div class="ztcfg2__rec">
        <div class="ztcfg2__rec-head">
          <span class="ztcfg2__rec-eyebrow">${escapeHtml(eyebrow)}</span>
          ${conf}
        </div>
        <div class="ztcfg2__rec-body">
          <div class="ztcfg2__rec-image">
            ${product.featured_image ? `<img src="${product.featured_image}" alt="${escapeHtml(product.title)}" loading="lazy" />` : ''}
          </div>
          <div class="ztcfg2__rec-meta">
            <h3 class="ztcfg2__rec-title">${escapeHtml(product.title)}</h3>
            <p class="ztcfg2__rec-price">${formatMoney(product.price)}</p>
            <ul class="ztcfg2__rec-why">
              ${(why || []).map((w) => `<li>${escapeHtml(w)}</li>`).join('')}
            </ul>
          </div>
        </div>
      </div>`;
  }

  // ── Step 3: Venting ──────────────────────────────────────────────────────
  function renderVenting() {
    const wrap = root.querySelector('[data-ztcfg2-step-venting]');
    if (!wrap) return;
    const v = state.venting;
    const isDD = effectiveHob()?.key === HOB_DD?.key;

    if (!isDD) {
      // Should be skipped by visibleSteps(), but render an info card just in case.
      wrap.innerHTML = `
        <div class="ztcfg2__view-head">
          <h1 class="ztcfg2__view-title">No built-in venting needed</h1>
          <p class="ztcfg2__view-sub">Your selected hob pairs with a standard range hood. Continue to fit and installation.</p>
        </div>`;
      return;
    }

    const g1 = chipGroup(
      'Can air be vented outside?',
      'Ducted extraction is the lowest-maintenance option — no filter to replace.',
      [
        { value: 'yes_wall', label: 'Yes — wall exit nearby' },
        { value: 'yes_floor', label: 'Yes — through floor / crawlspace' },
        { value: 'maybe', label: 'Maybe' },
        { value: 'no', label: 'No' },
      ],
      v.outsideVenting,
      (val) => { v.outsideVenting = val; v.partsAdjust = {}; renderVenting(); updateRail(); updateTotals(); }
    );
    const g2 = chipGroup(
      'Approximate route length',
      'From the hob outlet to where the air leaves the kitchen.',
      [
        { value: 'under_1m', label: 'Under 1 m' },
        { value: '1_3m', label: '1–3 m' },
        { value: 'over_3m', label: '3 m +' },
        { value: 'unknown', label: 'Not sure' },
      ],
      v.routeLength,
      (val) => { v.routeLength = val; v.partsAdjust = {}; renderVenting(); updateRail(); updateTotals(); }
    );
    const g3 = chipGroup(
      'How many 90° bends?',
      'Each bend reduces airflow — keep them low when possible.',
      [
        { value: '0_1', label: '0–1' },
        { value: '2_3', label: '2–3' },
        { value: '4_plus', label: '4+' },
        { value: 'unknown', label: 'Not sure' },
      ],
      v.bends,
      (val) => { v.bends = val; v.partsAdjust = {}; renderVenting(); updateRail(); updateTotals(); }
    );
    const g4 = chipGroup(
      'Cabinet & plinth setup below the hob',
      null,
      [
        { value: 'standard_plinth', label: 'Standard cabinet + plinth' },
        { value: 'drawer', label: 'Drawer cabinet' },
        { value: 'island', label: 'Kitchen island' },
        { value: 'unknown', label: 'Not sure' },
      ],
      v.cabinetType,
      (val) => { v.cabinetType = val; v.partsAdjust = {}; renderVenting(); updateRail(); updateTotals(); }
    );

    let recHtml = '';
    let partsHtml = '';
    let methodSwitchHtml = '';
    if (canProceed('venting')) {
      const rec = recommendVenting();
      const methodLabel =
        COPY_METHODS[rec.method]?.label ||
        ({
          ducted_extraction: 'Ducted extraction · Vent outside',
          ducted_recirculation: 'Ducted recirculation · Plinth filter',
          pipeless_recirculation: 'Pipeless recirculation · Rear cabinet',
        }[rec.method]);

      const partsTotal = Object.entries(rec.parts).reduce((s, [k, q]) => {
        const p = productByKey[k];
        return p ? s + (p.price || 0) * q : s;
      }, 0);

      recHtml = `
        <div class="ztcfg2__rec">
          <div class="ztcfg2__rec-head">
            <span class="ztcfg2__rec-eyebrow">Recommended venting</span>
            <span class="ztcfg2__rec-confidence is-${rec.confidence}">${rec.confidence === 'green' ? 'Best fit' : rec.confidence === 'yellow' ? 'Workable · installer should confirm' : 'Needs review'}</span>
          </div>
          <div class="ztcfg2__rec-body" style="grid-template-columns: 1fr;">
            <div class="ztcfg2__rec-meta" style="padding: 18px 24px;">
              <h3 class="ztcfg2__rec-title">${escapeHtml(methodLabel)}</h3>
              <ul class="ztcfg2__rec-why">
                ${rec.why.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}
              </ul>
            </div>
          </div>
        </div>`;

      const methodOptions = [
        { value: 'ducted_extraction', label: 'Vent outside' },
        { value: 'ducted_recirculation', label: 'Ducted filter' },
        { value: 'pipeless_recirculation', label: 'Pipeless filter' },
      ];
      methodSwitchHtml = `
        <div class="ztcfg2__qgroup">
          <h3 class="ztcfg2__qgroup-q">Override venting method</h3>
          <p class="ztcfg2__qgroup-help">Most users should keep the recommendation, but you can swap if your kitchen has a constraint we missed.</p>
          <div class="ztcfg2__chips">
            ${methodOptions.map((o) => `<button type="button" class="ztcfg2__chip ${rec.method === o.value ? 'is-active' : ''}" data-method="${o.value}">${o.label}</button>`).join('')}
          </div>
        </div>`;

      const partsRows = Object.entries(rec.parts)
        .map(([k, q]) => {
          const p = productByKey[k];
          if (!p) return '';
          return `
            <div class="ztcfg2__part" data-part="${k}">
              <div class="ztcfg2__part-thumb">${p.featured_image_thumb ? `<img src="${p.featured_image_thumb}" alt="" loading="lazy" />` : ''}</div>
              <div class="ztcfg2__part-meta">
                <p class="ztcfg2__part-title">${escapeHtml(p.title)}</p>
                <p class="ztcfg2__part-sub">${formatMoney(p.price)} · ${formatMoney(p.price * q)} total</p>
              </div>
              <div class="ztcfg2__part-qty">
                <button type="button" data-qty-dec aria-label="Decrease">${ICONS.minus}</button>
                <span class="ztcfg2__part-qty-val">${q}</span>
                <button type="button" data-qty-inc aria-label="Increase">${ICONS.plus}</button>
              </div>
            </div>`;
        })
        .join('');

      partsHtml = `
        <div class="ztcfg2__parts">
          <div class="ztcfg2__parts-head">
            <h3>Your venting parts</h3>
            <span class="ztcfg2__parts-total">${formatMoney(partsTotal)}</span>
          </div>
          ${partsRows || '<p class="ztcfg2__empty">No parts needed for this method.</p>'}
        </div>`;
    }

    wrap.innerHTML = `
      <div class="ztcfg2__view-head">
        <h1 class="ztcfg2__view-title">${escapeHtml(COPY_STEPS.venting?.heading || 'Set up venting')}</h1>
        <p class="ztcfg2__view-sub">${escapeHtml(COPY_STEPS.venting?.sub || "The downdraft hob can vent outside, recirculate through a filter, or filter pipelessly. We'll pick the best option from your answers.")}</p>
      </div>
      ${g1.html}${g2.html}${g3.html}${g4.html}
      ${recHtml}
      ${methodSwitchHtml}
      ${partsHtml}`;

    g1.bind(wrap); g2.bind(wrap); g3.bind(wrap); g4.bind(wrap);

    wrap.querySelectorAll('[data-method]').forEach((b) => {
      b.addEventListener('click', () => {
        v.methodOverride = b.dataset.method;
        v.partsAdjust = {};
        renderVenting(); updateTotals();
      });
    });
    wrap.querySelectorAll('[data-part]').forEach((row) => {
      const k = row.dataset.part;
      row.querySelector('[data-qty-inc]').addEventListener('click', () => {
        v.partsAdjust[k] = (v.partsAdjust[k] || 0) + 1;
        renderVenting(); updateTotals();
      });
      row.querySelector('[data-qty-dec]').addEventListener('click', () => {
        v.partsAdjust[k] = (v.partsAdjust[k] || 0) - 1;
        renderVenting(); updateTotals();
      });
    });
  }

  // ── Step 4: Fit ──────────────────────────────────────────────────────────
  function renderFit() {
    const wrap = root.querySelector('[data-ztcfg2-step-fit]');
    if (!wrap) return;
    const f = state.fit;
    const isDD = effectiveHob()?.key === HOB_DD?.key;

    const fields = [
      { id: 'currentHobWidth', label: 'Current hob width', unit: 'mm' },
      { id: 'currentHobDepth', label: 'Current hob depth', unit: 'mm' },
      { id: 'cutoutWidth', label: 'Cut-out width', unit: 'mm' },
      { id: 'cutoutDepth', label: 'Cut-out depth', unit: 'mm' },
    ];
    const ddFields = isDD ? [
      { id: 'cabinetWidth', label: 'Cabinet width below hob', unit: 'mm' },
      { id: 'spaceBelow', label: 'Space below hob', unit: 'mm' },
    ] : [];

    const fieldsHtml = [...fields, ...ddFields]
      .map((field) => `
        <div class="ztcfg2__field">
          <label class="ztcfg2__field-label" for="ztcfg2-${field.id}">${escapeHtml(field.label)}</label>
          <div class="ztcfg2__field-suffix">
            <input id="ztcfg2-${field.id}" class="ztcfg2__field-input" type="number" inputmode="numeric" min="0" value="${escapeHtml(f[field.id] || '')}" data-fit-field="${field.id}" />
            <span class="ztcfg2__field-unit">${field.unit}</span>
          </div>
        </div>`).join('');

    const mountG = chipGroup(
      'Preferred mounting',
      'Surface mounting drops the hob into the cut-out (the common method). Flush mounting needs a precise recess so the glass sits level with the worktop.',
      [
        { value: 'surface', label: 'Surface' },
        { value: 'flush', label: 'Flush' },
        { value: 'unknown', label: 'Not sure' },
      ],
      f.mounting,
      (v) => { f.mounting = v; renderFit(); }
    );

    const hob = effectiveHob();
    const hobK = vizHobKey();
    const target = hobK ? COPY_SPECS[hobK]?.label : '';

    let fitNotice = '';
    const cw = parseInt(f.cutoutWidth, 10);
    const cd = parseInt(f.cutoutDepth, 10);
    if (cw && cd && hobK) {
      const spec = COPY_SPECS[hobK] || {};
      const ok =
        Math.abs(cw - spec.w) <= TOL_W &&
        Math.abs(cd - spec.d) <= TOL_D;
      const tmpl = ok
        ? (COPY_NOTICES.fit_match || 'Cut-out matches the %hob% spec (%spec%).')
        : (COPY_NOTICES.fit_mismatch || "Your cut-out doesn't match the %hob% spec (%spec%). Confirm with your installer or kitchen supplier before ordering.");
      const text = tmpl
        .replace(/%hob%/g, hob?.title || '')
        .replace(/%spec%/g, target || '');
      fitNotice = `<div class="ztcfg2__notice ${ok ? 'ztcfg2__notice--info' : ''}">${escapeHtml(text)}</div>`;
    }

    const fitHeading = COPY_STEPS.fit?.heading || 'Will it fit?';
    const fitSub = COPY_STEPS.fit?.sub || 'Optional, but worth doing — your installer will need these numbers.';
    wrap.innerHTML = `
      <div class="ztcfg2__view-head">
        <h1 class="ztcfg2__view-title">${escapeHtml(fitHeading)}</h1>
        <p class="ztcfg2__view-sub">${escapeHtml(fitSub)} ${target ? `Target cut-out for the ${escapeHtml(hob.title)}: <strong>${escapeHtml(target)}</strong>.` : ''}</p>
      </div>
      <div class="ztcfg2__qgroup">
        <h3 class="ztcfg2__qgroup-q">Measurements</h3>
        <p class="ztcfg2__qgroup-help">All in millimetres. Leave blank if you're not sure — your installer can confirm later.</p>
        <div class="ztcfg2__fields">${fieldsHtml}</div>
      </div>
      ${mountG.html}
      ${fitNotice}
      <div class="ztcfg2__notice">${escapeHtml(COPY_NOTICES.electrical || 'Electrical connection must be performed by a certified electrician. Send the installation manual to your kitchen supplier or installer before cutting the worktop.')}</div>`;

    mountG.bind(wrap);
    wrap.querySelectorAll('[data-fit-field]').forEach((inp) => {
      inp.addEventListener('input', (e) => {
        f[inp.dataset.fitField] = e.target.value;
        // Re-render only fitNotice — full rerender preserves focus issue, so use targeted update
        renderFit();
        const focused = wrap.querySelector(`[data-fit-field="${inp.dataset.fitField}"]`);
        if (focused) {
          focused.focus();
          focused.setSelectionRange(focused.value.length, focused.value.length);
        }
      });
    });
  }

  // ── Step 5: Cookware ─────────────────────────────────────────────────────
  function renderCookware() {
    const wrap = root.querySelector('[data-ztcfg2-step-cookware]');
    if (!wrap) return;
    const c = state.cookware;

    const g1 = chipGroup(
      'Do you want temperature control from day one?',
      'Ztove cookware unlocks 0–250 °C precision. Without it, you get a high-end induction hob with normal 1–9 levels.',
      [
        { value: 'yes', label: 'Yes — give me the full experience' },
        { value: 'maybe', label: 'Maybe later' },
        { value: 'no', label: "No, I'll use my existing pots" },
      ],
      c.wantsTempControl,
      (v) => { c.wantsTempControl = v; c.bundleOverride = null; renderCookware(); updateRail(); updateTotals(); }
    );

    const styleG = multiChipGroup(
      'What do you cook most? (pick any)',
      null,
      [
        { value: 'everyday', label: 'Everyday meals' },
        { value: 'frying', label: 'Frying meat / fish' },
        { value: 'eggs', label: 'Pancakes / eggs / non-stick' },
        { value: 'pasta', label: 'Pasta / rice / sauces' },
        { value: 'deep_frying', label: 'Deep frying' },
        { value: 'sous_vide', label: 'Sous vide' },
      ],
      c.cookingStyles,
      () => { c.bundleOverride = null; renderCookware(); updateRail(); updateTotals(); }
    );

    const g2 = chipGroup(
      'Household size',
      null,
      [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3_4', label: '3–4' },
        { value: '5_plus', label: '5+' },
      ],
      c.householdSize,
      (v) => { c.householdSize = v; c.bundleOverride = null; renderCookware(); updateRail(); updateTotals(); }
    );

    const head = `
      <div class="ztcfg2__view-head">
        <h1 class="ztcfg2__view-title">${escapeHtml(COPY_STEPS.cookware?.heading || 'Add cookware')}</h1>
        <p class="ztcfg2__view-sub">${escapeHtml(COPY_STEPS.cookware?.sub || 'The hob is half the magic. Smart cookware is what makes 0–250 °C cooking possible.')}</p>
      </div>`;

    let recHtml = '';
    let bundlesHtml = '';
    if (c.wantsTempControl !== null) {
      const rec = recommendCookware();
      if (rec.bundles.length) {
        recHtml = `
          <div class="ztcfg2__rec">
            <div class="ztcfg2__rec-head">
              <span class="ztcfg2__rec-eyebrow">${c.bundleOverride ? 'Your pick' : 'Recommended for you'}</span>
              <span class="ztcfg2__rec-confidence is-${rec.confidence}">${rec.confidence === 'green' ? 'Solid pick' : rec.confidence === 'yellow' ? 'Worth knowing' : 'Needs review'}</span>
            </div>
            <div class="ztcfg2__rec-body" style="grid-template-columns: 1fr;">
              <div class="ztcfg2__rec-meta" style="padding: 18px 24px;">
                <h3 class="ztcfg2__rec-title">${rec.bundles.map((k) => escapeHtml(productByKey[k]?.title || '')).join(' + ')}</h3>
                <ul class="ztcfg2__rec-why">${rec.why.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>
              </div>
            </div>
          </div>`;
      }

      const bundleProducts = products.filter((p) => p.role === 'bundle');
      const noneActive = c.bundleOverride === 'none';
      const recSet = new Set(rec.bundles);
      const isPickedRec = !c.bundleOverride;

      bundlesHtml = `
        <div class="ztcfg2__qgroup">
          <h3 class="ztcfg2__qgroup-q">Or pick a different cookware option</h3>
          <div class="ztcfg2__bundles">
            ${bundleProducts.map((b) => {
              const isActive = c.bundleOverride === b.key || (isPickedRec && recSet.size === 1 && recSet.has(b.key));
              return `
                <button type="button" class="ztcfg2__bundle ${isActive ? 'is-active' : ''}" data-bundle="${b.key}">
                  <div class="ztcfg2__bundle-image">${b.featured_image ? `<img src="${b.featured_image}" alt="${escapeHtml(b.title)}" loading="lazy" />` : ''}</div>
                  <div class="ztcfg2__bundle-body">
                    <h4 class="ztcfg2__bundle-title">${escapeHtml(b.title)}</h4>
                    <p class="ztcfg2__bundle-desc">${escapeHtml(b.description_text || '')}</p>
                  </div>
                  <div class="ztcfg2__bundle-foot">
                    <span class="ztcfg2__bundle-price">${formatMoney(b.price)}</span>
                    <span class="ztcfg2__bundle-state">${isActive ? 'Selected' : 'Select'}${ICONS.check}</span>
                  </div>
                </button>`;
            }).join('')}
            <button type="button" class="ztcfg2__bundle ${noneActive ? 'is-active' : ''}" data-bundle="none" style="grid-column: 1 / -1;">
              <div class="ztcfg2__bundle-body" style="padding: 20px;">
                <h4 class="ztcfg2__bundle-title">No cookware right now</h4>
                <p class="ztcfg2__bundle-desc">Use your existing induction cookware. You can add Ztove cookware any time later.</p>
              </div>
              <div class="ztcfg2__bundle-foot">
                <span class="ztcfg2__bundle-price">— kr</span>
                <span class="ztcfg2__bundle-state">${noneActive ? 'Selected' : 'Select'}${ICONS.check}</span>
              </div>
            </button>
          </div>
        </div>`;
    }

    wrap.innerHTML = `${head}${g1.html}${styleG.html}${g2.html}${recHtml}${bundlesHtml}`;
    g1.bind(wrap); styleG.bind(wrap); g2.bind(wrap);
    wrap.querySelectorAll('[data-bundle]').forEach((b) => {
      b.addEventListener('click', () => {
        c.bundleOverride = b.dataset.bundle;
        renderCookware(); updateTotals();
      });
    });
  }

  // ── Step 6: Summary ──────────────────────────────────────────────────────
  function renderSummary() {
    const wrap = root.querySelector('[data-ztcfg2-step-summary]');
    if (!wrap) return;

    const items = getCartItems();
    const subtotal = getSubtotal();
    const conf = overallConfidence();
    const confLabel =
      conf === 'green' ? (COPY_CONF.summary_green || 'Ready to order')
      : conf === 'yellow' ? (COPY_CONF.summary_yellow || 'Likely fit · installer should confirm')
      : (COPY_CONF.summary_red || 'Needs review');

    const grouped = items.reduce((acc, i) => {
      acc[i._section] = acc[i._section] || [];
      acc[i._section].push(i);
      return acc;
    }, {});

    const sectionToStep = { Hob: 'hob', Venting: 'venting', Cookware: 'cookware' };
    const recs = computeRecommendations();
    const sectionWhy = {
      Hob: recs.hob.why,
      Venting: recs.venting?.why || [],
      Cookware: recs.cookware.why,
    };

    const blocksHtml = Object.entries(grouped).map(([section, lines]) => `
      <div class="ztcfg2__sum-block">
        <div class="ztcfg2__sum-head">
          <h3>${section}</h3>
          <button type="button" class="ztcfg2__sum-edit" data-edit-step="${sectionToStep[section]}">Edit</button>
        </div>
        <div class="ztcfg2__sum-body">
          ${lines.map((line) => {
            const p = line._product;
            return `
              <div class="ztcfg2__sum-line">
                <div class="ztcfg2__part-thumb">${p.featured_image_thumb ? `<img src="${p.featured_image_thumb}" alt="" loading="lazy" />` : ''}</div>
                <div class="ztcfg2__sum-line-meta">
                  <p class="ztcfg2__sum-line-title">${escapeHtml(p.title)}</p>
                  <p class="ztcfg2__sum-line-sub">${formatMoney(p.price)} · Qty ${line.quantity}</p>
                </div>
                <span class="ztcfg2__sum-line-price">${formatMoney(p.price * line.quantity)}</span>
              </div>`;
          }).join('')}
        </div>
        ${sectionWhy[section]?.length ? `<div class="ztcfg2__sum-why"><strong>Why:</strong> ${sectionWhy[section].map(escapeHtml).join(' ')}</div>` : ''}
      </div>`).join('');

    wrap.innerHTML = `
      <div class="ztcfg2__view-head">
        <h1 class="ztcfg2__view-title">${escapeHtml(COPY_STEPS.summary?.heading || 'Your Ztove setup')}</h1>
        <p class="ztcfg2__view-sub">${escapeHtml(COPY_STEPS.summary?.sub || 'A clean plan you can review, share with your installer, and add to your bag.')}</p>
      </div>
      <div class="ztcfg2__rec" style="margin-bottom: 16px;">
        <div class="ztcfg2__rec-head">
          <span class="ztcfg2__rec-eyebrow">Overall confidence</span>
          <span class="ztcfg2__rec-confidence is-${conf}">${escapeHtml(confLabel)}</span>
        </div>
      </div>
      <div class="ztcfg2__summary">
        ${blocksHtml || '<div class="ztcfg2__sum-block"><div class="ztcfg2__sum-body"><p class="ztcfg2__empty">Nothing selected yet — go back and pick a hob.</p></div></div>'}
        <div class="ztcfg2__sum-totals">
          <div class="ztcfg2__sum-row ztcfg2__sum-row--total">
            <span>Subtotal</span><span>${formatMoney(subtotal)}</span>
          </div>
          <p class="ztcfg2__sum-shipping">${escapeHtml(copy.shipping_note || '')}</p>
        </div>
        <div class="ztcfg2__installer">
          <details>
            <summary>Installer & order summary</summary>
            <div class="ztcfg2__installer-body">${escapeHtml(buildInstallerSummary())}</div>
            <div class="ztcfg2__installer-actions">
              <button type="button" class="ztcfg2__btn ztcfg2__btn--ghost" data-copy-installer>Copy summary</button>
              ${copy.installer_email ? `<a class="ztcfg2__btn ztcfg2__btn--ghost" href="${mailtoLink()}">Email summary</a>` : ''}
              <span class="ztcfg2__copied" data-copied>Copied</span>
            </div>
          </details>
        </div>
      </div>`;

    wrap.querySelectorAll('[data-edit-step]').forEach((btn) =>
      btn.addEventListener('click', () => setStep(btn.dataset.editStep))
    );
    wrap.querySelector('[data-copy-installer]')?.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(buildInstallerSummary());
        const flag = wrap.querySelector('[data-copied]');
        if (flag) {
          flag.classList.add('is-visible');
          setTimeout(() => flag.classList.remove('is-visible'), 2000);
        }
      } catch (e) {
        console.error(e);
      }
    });
  }

  // ── Installer summary + email ────────────────────────────────────────────
  function buildInstallerSummary() {
    const recs = computeRecommendations();
    const hob = effectiveHob();
    const f = state.fit;
    const k = state.kitchen;
    const lines = [];

    lines.push('ZTOVE INSTALLATION SUMMARY');
    lines.push('--------------------------');
    lines.push('');
    lines.push('CUSTOMER KITCHEN');
    lines.push(`- Project: ${k.projectType || '—'}`);
    lines.push(`- Placement: ${k.placement || '—'}`);
    lines.push(`- Existing extractor: ${k.existingExtractor || '—'}`);
    lines.push('');
    lines.push('HOB');
    if (hob) {
      lines.push(`- Model: ${hob.title}`);
      const hobK = vizHobKey();
      const specLabel = hobK ? COPY_SPECS[hobK]?.label : null;
      lines.push(`- Target cut-out: ${specLabel || '—'}`);
    }
    lines.push(`- Mounting: ${f.mounting || '—'}`);
    lines.push(`- Current hob: ${f.currentHobWidth || '—'} × ${f.currentHobDepth || '—'} mm`);
    lines.push(`- Cut-out measured: ${f.cutoutWidth || '—'} × ${f.cutoutDepth || '—'} mm`);
    lines.push('');
    if (recs.isDowndraft && recs.venting) {
      lines.push('VENTING');
      lines.push(`- Method: ${recs.venting.method}`);
      lines.push(`- Outside venting: ${state.venting.outsideVenting || '—'}`);
      lines.push(`- Route length: ${state.venting.routeLength || '—'}`);
      lines.push(`- 90° bends: ${state.venting.bends || '—'}`);
      lines.push(`- Cabinet/plinth: ${state.venting.cabinetType || '—'}`);
      lines.push(`- Cabinet width: ${f.cabinetWidth || '—'} mm`);
      lines.push(`- Space below hob: ${f.spaceBelow || '—'} mm`);
      lines.push('- Parts:');
      Object.entries(recs.venting.parts).forEach(([key, q]) => {
        const p = productByKey[key];
        if (p) lines.push(`  · ${q} × ${p.title}`);
      });
      lines.push('');
    }
    lines.push('COOKWARE');
    effectiveBundles().forEach((b) => lines.push(`- ${b.title}`));
    if (!effectiveBundles().length) lines.push('- None');
    lines.push('');
    lines.push('NOTES');
    lines.push('- Electrical connection must be by a certified electrician.');
    lines.push('- Hob must be installed in an approved kitchen cabinet/worktop.');
    lines.push('- Validate cut-out dimensions before cutting the worktop.');
    if (recs.isDowndraft) {
      lines.push('- For downdraft: confirm cabinet width and clearance below hob.');
      lines.push('- Keep duct length and bends low for best performance.');
    }
    return lines.join('\n');
  }

  function mailtoLink() {
    const to = copy.installer_email || '';
    const subject = encodeURIComponent('Ztove installation summary');
    const body = encodeURIComponent(buildInstallerSummary());
    return `mailto:${encodeURIComponent(to)}?subject=${subject}&body=${body}`;
  }

  // ── Add all to cart ──────────────────────────────────────────────────────
  async function addAllToCart() {
    if (state.submitting) return;
    const items = getCartItems().filter((i) => i.id).map((i) => ({ id: i.id, quantity: i.quantity }));
    if (!items.length) return;

    const btn = root.querySelector('[data-ztcfg2-primary]');
    const labelEl = root.querySelector('[data-ztcfg2-primary-label]');
    btn.classList.add('is-loading');
    state.submitting = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(await res.text());

      const attributes = {
        'Configurator': 'v2',
        'Project type': state.kitchen.projectType || '',
        'Placement': state.kitchen.placement || '',
        'Cut-out W (mm)': state.fit.cutoutWidth || '',
        'Cut-out D (mm)': state.fit.cutoutDepth || '',
        'Mounting': state.fit.mounting || '',
      };
      if (effectiveHob()?.key === HOB_DD?.key) {
        attributes['Venting method'] = recommendVenting().method;
      }
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });

      window.location.href = '/cart';
    } catch (err) {
      console.error('[ztcfg2] add failed', err);
      labelEl.textContent = 'Could not add — try again';
      btn.classList.remove('is-loading');
      state.submitting = false;
      setTimeout(() => { labelEl.textContent = 'Add everything to bag'; }, 3000);
    }
  }

  // ── Wire up ──────────────────────────────────────────────────────────────
  const primaryBtn = root.querySelector('[data-ztcfg2-primary]');
  if (primaryBtn) {
    primaryBtn.addEventListener('click', () => {
      if (state.step === 'summary') addAllToCart();
      else nextStep();
    });
  }
  root.querySelector('[data-ztcfg2-back]')?.addEventListener('click', prevStep);
  root.querySelectorAll('[data-step-rail]').forEach((el) => {
    el.addEventListener('click', () => {
      if (el.classList.contains('is-clickable')) setStep(el.dataset.stepRail);
    });
  });

  renderKitchen();
  updateRail();
  updateTotals();
})();
